<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/dialog'
import GbButton from '@/components/GbButton.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { threadsUrls } from '@/util/links'
import GenerationProgress from './GenerationProgress.vue'
import { GENERATED_ASSESSMENT_EVENTS } from './event_names'
import type { GenerationStageKey } from './generation_phases'
import type { GenerationOutcomeKind, GenerationRequest } from './useAssessmentGeneration'

const MAX_DIRECTIONS_LENGTH = 500

type FocusTarget = 'directions' | 'cancel' | 'reload' | 'generate' | 'view'

// The run belongs to the page, so these are its state, held there to survive closing the modal.
const props = withDefaults(defineProps<{
  open: boolean
  targetLabel: string
  targetText?: string
  isRunning?: boolean
  stage?: GenerationStageKey
  tick?: number
  detail?: string
  resultUuid?: string | null
  errorMessage?: string | null
  errorKind?: GenerationOutcomeKind | null
}>(), {
  targetText: '',
  isRunning: false,
  stage: 'starting',
  tick: 0,
  detail: '',
  resultUuid: null,
  errorMessage: null,
  errorKind: null
})

const emit = defineEmits<{
  'update:open': [isOpen: boolean]
  generate: [request: GenerationRequest]
  reload: []
}>()

const analytics = getAnalyticsTracker()
const directions = ref('')
const contentRef = ref<HTMLElement | null>(null)

const isSettled = computed(() => !props.isRunning && !props.resultUuid)
// An unknown outcome may already be saved, so reloading answers it; generating again risks a duplicate.
const isReloadShown = computed(() => Boolean(props.errorMessage) && props.errorKind === 'unknown' && isSettled.value)
const isFormShown = computed(() => isSettled.value && !isReloadShown.value)
const closeLabel = computed(() => (isFormShown.value ? 'Cancel' : 'Close'))
const viewUrl = computed(() => (props.resultUuid ? threadsUrls.assessmentViewer(props.resultUuid) : ''))
// The control that acts on the alert, so a keyboard user lands on it when the alert appears.
const errorAction = computed<FocusTarget | null>(() => {
  if (!props.errorMessage || !isSettled.value) return null
  return isReloadShown.value ? 'reload' : 'generate'
})

// Each state change unmounts the control holding focus, which would drop it to the body.
async function moveFocusTo(target: FocusTarget) {
  await nextTick()
  contentRef.value?.querySelector<HTMLElement>(`[data-focus="${target}"]`)?.focus()
}

// Cleared on every open: the one modal serves every target on the page.
watch(() => props.open, (isOpen) => {
  if (isOpen) directions.value = ''
})
watch(() => props.isRunning, (isRunning) => {
  if (isRunning) moveFocusTo('cancel')
})
watch(() => props.resultUuid, (uuid) => {
  if (uuid) moveFocusTo('view')
})
watch(errorAction, (target) => {
  if (target) moveFocusTo(target)
})

// Reopening on an unresolved outcome doesn't re-run the errorAction watcher, so it is checked here.
function focusOnOpen(event: Event) {
  event.preventDefault()
  moveFocusTo(errorAction.value ?? (isFormShown.value ? 'directions' : 'cancel'))
}

function close() {
  emit('update:open', false)
}

// Doesn't close: the run's progress shows in this same modal.
function submit() {
  emit('generate', { teacherDirections: directions.value.trim() || null })
}

function trackView() {
  analytics.sendEvent(GENERATED_ASSESSMENT_EVENTS.CLICKED_VIEW)
}
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent
      class="max-h-[90vh] gap-0 rounded-sm bg-white p-0 text-gray-900 shadow-xl sm:max-w-[600px] sm:rounded-sm"
      data-testid="generate-assessment-modal"
      @open-auto-focus="focusOnOpen"
    >
      <div ref="contentRef">
        <DialogHeader class="border-b border-gray-300 px-4 py-4">
          <DialogTitle class="text-xl font-medium">Generate an assessment</DialogTitle>
          <DialogDescription class="sr-only">Generate an assessment for {{ targetLabel }}</DialogDescription>
        </DialogHeader>

        <!-- Capped so the footer stays reachable when the page is zoomed. -->
        <div class="max-h-[70vh] overflow-y-auto p-4 text-md">
          <template v-if="resultUuid">
            <p class="mb-3.5 text-lg font-medium">Your assessment is ready.</p>
            <!-- A new tab: the teacher is likely still working on this page, where the assessment also appears. -->
            <a
              :href="viewUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-block rounded-sm bg-blue-400 px-4 py-2.5 text-xl text-white no-underline hover:bg-blue-500 hover:text-white hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
              data-focus="view"
              data-testid="view-generated-assessment"
              @click="trackView"
            >View assessment</a>
          </template>

          <GenerationProgress v-else-if="isRunning" :stage="stage" :tick="tick" :detail="detail" />

          <template v-else-if="isFormShown">
            <!-- fs-exclude: teacher-authored IEP goal text. -->
            <div class="fs-exclude mb-4 rounded-xs border border-gray-300 bg-gray-100 px-3 py-2.5" data-testid="generation-target">
              <span class="mb-1 block font-bold text-gray-700">{{ targetLabel }}</span>
              {{ targetText }}
            </div>

            <label class="mb-1.5 block font-bold" for="generate-assessment-directions">
              Directions or suggestions to incorporate (optional)
            </label>
            <textarea
              id="generate-assessment-directions"
              v-model="directions"
              class="fs-exclude block w-full resize-y rounded-xs border border-gray-400 px-3 py-1.5 placeholder:text-gray-700 focus-visible:border-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400"
              rows="3"
              :maxlength="MAX_DIRECTIONS_LENGTH"
              aria-describedby="generate-assessment-hint generate-assessment-count"
              placeholder="e.g. sports and music contexts"
              data-focus="directions"
            />
            <div class="mt-1.5 flex gap-2.5 text-gray-700">
              <!-- The limit is stated in words because the counter is not a live region: announcing
                   it on every keystroke would interrupt the typing echo. -->
              <span id="generate-assessment-hint">
                Student interests, topics or themes, or supporting resources to include.
                Maximum {{ MAX_DIRECTIONS_LENGTH }} characters.
              </span>
              <span id="generate-assessment-count" class="ml-auto whitespace-nowrap tabular-nums">{{ directions.length }}/{{ MAX_DIRECTIONS_LENGTH }}</span>
            </div>

            <p class="mt-4 text-gray-700">
              This feature uses generative AI. Review content to ensure accuracy, relevance, and objectivity.
            </p>
          </template>

          <!-- Last in the body, so the reason sits directly above the button that acts on it. -->
          <div
            v-if="errorMessage"
            class="mb-2.5 mt-4 rounded-xs border border-red-100 bg-red-100 p-4 text-red-300"
            role="alert"
            data-testid="generation-error"
          >{{ errorMessage }}</div>
        </div>

        <DialogFooter class="gap-2 border-t border-gray-300 px-4 py-4" data-testid="generate-assessment-footer">
          <GbButton gb-theme="blueTertiary" data-focus="cancel" @click="close">{{ closeLabel }}</GbButton>
          <GbButton v-if="isReloadShown" gb-theme="bluePrimary" data-focus="reload" @click="emit('reload')">Reload page</GbButton>
          <GbButton v-else-if="isSettled" gb-theme="aiPrimary" data-focus="generate" @click="submit">Generate</GbButton>
        </DialogFooter>
      </div>
    </DialogContent>
  </Dialog>
</template>
